import { Container, Row, Col, Button } from "react-bootstrap";
import { useState, useEffect, useCallback, Suspense, lazy } from "react";
import apiCall from "components/Api/api";
import SightingModal from "./feeds1/SightingModal";
import CommentSectionModal from "./feeds1/CommentSectionModal";
import PostSkeleton from "./feeds1/PostSkeleton";

// Lazy load components
const SightingFeedSection = lazy(() => import("./feeds1/SightingFeedSection"));
const SharedSafariFeed = lazy(() => import("./feeds1/SharedSafariFeed"));
const Post = lazy(() => import("./feeds1/Post"));
const PackageFeedSection = lazy(() => import("./feeds1/PackageFeedSection"));

const Feed1 = () => {
  const [feeds, setFeeds] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSightingData, setSelectedSightingData] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    page: 1,
    pageSize: 6,
    total_page: 1,
  });

  // Memoized handlers
  const handleSightingClick = useCallback((sightingData, clickedFeedIndex) => {
    setSelectedSightingData({ sightingData, clickedFeedIndex });
    setModalOpen(true);
  }, []);

  const handleCommentClick = useCallback((post) => {
    setSelectedPost(post);
    setCommentModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedSightingData(null);
  }, []);

  const handleCloseCommentModal = useCallback(() => {
    setCommentModalOpen(false);
    setSelectedPost(null);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchPackageView = async () => {
      try {
        setLoadingFeed(true);
        const response = await apiCall({
          url: `feeds?page=${pageIndex}&pageSize=${summary.pageSize}`,
          method: "GET",
          signal: controller.signal,
        });

        if (response.status !== 200) {
          throw new Error(`Unexpected status code: ${response.status}`);
        }

        const apiFeeds = response?.data?.data?.feeds || [];
        const apiSummary = response?.data?.data?.summary;
        const currentPage = Number(apiSummary?.query_params?.pages || pageIndex);

        setFeeds((prevFeeds) => [...prevFeeds, ...apiFeeds]);

        setSummary({
          total: apiSummary?.total || 0,
          page: currentPage,
          pageSize: apiSummary?.pageSize || 6,
          total_page: apiSummary?.total_page || 1,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Feed fetch error:", error);
        }
      } finally {
        setLoadingFeed(false);
      }
    };

    fetchPackageView();
    return () => controller.abort();
  }, [pageIndex]);

  // Feed rendering logic extracted
  const renderFeedItem = useCallback((item) => {
    const { objective, feed, package_feeds, sighting_feeds } = item;

    switch (objective) {
      case "share_safari":
        return <SharedSafariFeed safari={feed} />;
      case "posts":
        return <Post post={feed} onCommentClick={() => handleCommentClick(feed)} />;
      case "package":
        return Array.isArray(package_feeds) ? (
          <PackageFeedSection packageFeeds={package_feeds} />
        ) : null;
      case "sighting":
        return Array.isArray(sighting_feeds) ? (
          <SightingFeedSection
            feeds={sighting_feeds}
            onSightingClick={(data, index) =>
              handleSightingClick(sighting_feeds, index)
            }
          />
        ) : null;
      default:
        return null;
    }
  }, [handleCommentClick, handleSightingClick]);

  return (
    <Container className="min-vh-100 py-4 mt-5">
      {loadingFeed && feeds.length === 0 ? (
        <Row>
          {[...Array(3)].map((_, idx) => (
            <Col xs={12} md={9} xl={8} xxl={7} key={idx} className="mx-auto">
              <PostSkeleton />
            </Col>
          ))}
        </Row>
      ) : feeds.length > 0 ? (
        <>
          <Row>
              {feeds.map((item) => (
                <Col xs={12} md={9} xl={8} xxl={7} key={item.id} className="mx-auto">
                  <Suspense fallback={<PostSkeleton />}>
                    {renderFeedItem(item)}
                  </Suspense>
                </Col>
              ))}
          </Row>

          {summary.page < summary.total_page && (
            <div className="text-center mb-md-5 mt-md-1 mb-sm-5 mb-5">
              <Button
                onClick={() => setPageIndex((prev) => prev + 1)}
                className="btn-success bg-green"
              >
                <i className="fas fa-plus me-2"></i>
                Load More
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center">
          <h3>No feeds found.</h3>
        </div>
      )}

      <SightingModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        sightingFeeds={selectedSightingData?.sightingData || []}
        initialIndex={selectedSightingData?.clickedFeedIndex || 0}
      />

      <CommentSectionModal
        show={commentModalOpen}
        onHide={handleCloseCommentModal}
        post={selectedPost}
      />
    </Container>
  );
};

export default Feed1;
